/**
 * Created by renlaydel on 28/11/2017.
 */
import moment from 'moment';
import uuid from 'uuid/v4';
import keyBy from 'lodash/keyBy';
import { takeEvery, takeLatest, select, call, put } from 'redux-saga/effects';
import { browserHistory } from 'react-router';
import { change, arrayRemove } from 'redux-form';
import * as sagasCommon from '../../sagasCommon';
import * as apiCommon from '../../../services/apiCommon';
import * as apiInventoryReturn from '../../../services/purchase/return/api_inventory_return';
import * as apiPurchaseCommon from '../../../services/purchase/common/api_purchase';
import * as apiTaxCode from '../../../services/tax/apiTaxCode';
import * as apiExchangeRate from '../../../services/exchange_rate/api_exchange_rate';
import { initializeItemLine, updateAvailableOnHand } from '../saga_purchase';
import {
  GET_INVENTORY_RETURN,
  SAVE_INVENTORY_RETURN,
  FETCH_LIST_INVENTORY_RETURN,
  GET_INVENTORY_RETURN_LINE_TAX_GROUP,
  GET_INVENTORY_RETURN_LINE_ITEM_LOCATION,
  GET_INVENTORY_RETURN_EXCHANGE_RATE,
  GET_ALL_INVENTORY_RETURN_LINES_ITEM_LOCATION,
} from '../../../constants/constant_saga_types';
import { GENERATE_PURCHASE_RETURN } from '../../../constants/constant_action_types';
import { InventoryReturnForm as form } from '../../../constants/constant_form_names';
import { getFormLines } from '../../../components/admin/purchase/return/InventoryReturnSelector';
import { INVENTORY_RETURN } from '../../../constants/constant_doc_types';
import {
  getOpenTransactionStatus,
  getPendingInventoryStatus,
} from '../../../common/selectors/selector_common';
import { getPreferenceSettings } from '../../../actions/actionCreatorsAdmin';

export function* getInventoryReturnSaga(feathersApp) {
  yield takeEvery(GET_INVENTORY_RETURN, callGetInventoryReturn, feathersApp);
}

export function* callGetInventoryReturn(feathersApp, action) {
  try {
    const { user, purchase } = yield select();
    const { receipt } = purchase;
    const { id } = action;

    yield sagasCommon.showProgressDataById();
    yield put(getPreferenceSettings(['enable_item_part_quantity']));

    if (id) {
      const data = yield call(
        apiInventoryReturn.fetchInventoryReturnById,
        feathersApp,
        { id, user },
      );
      yield sagasCommon.doneDataById(data);
    } else if (receipt.generate_return) {
      yield put({ type: GENERATE_PURCHASE_RETURN, generateReturn: false });
    } else {
      const contactId = user.id || user._id;
      const primaryContactNumber = (user.contact_numbers || []).find(
        (number) => number.primary,
      );
      const status = yield select(getOpenTransactionStatus);
      const data = {
        status,
        status_id: status.id,
        exchange_rate: 1,
        return_type: 'other',
        transaction_date: moment().format(),
        purchase_contact_id: contactId,
        purchase_contact_email: user.email,
        purchase_contact_number: (primaryContactNumber || {}).value,
      };
      yield sagasCommon.doneDataById(data);
    }
  } catch (e) {
    yield sagasCommon.showErrorDataById(e, true);
  }
}

export function* buildPurchaseReturn(feathersApp, data) {
  let itemLines = [];
  const { user } = yield select();
  const pendingStatus = yield select(getPendingInventoryStatus);
  const openStatus = yield select(getOpenTransactionStatus);
  const purchaseReceipt = JSON.parse(JSON.stringify(data));
  purchaseReceipt.lines.forEach((prLine) => {
    const line = {
      item: prLine.item,
      item_id: prLine.item_id,
      item_type: prLine.item_type,

      tax_group: prLine.tax_group,
      tax_group_id: prLine.tax_group_id,

      unit_cost: prLine.unit_cost,
      available_pre_return: prLine.available_pre_receipt,
      quantity: prLine.quantity,
      receipt_quantity: prLine.quantity,
      po_quantity: prLine.po_quantity,
      tax_rate: prLine.tax_rate,
      total_ex_tax: prLine.total_ex_tax,
      total: prLine.total,
      pr_line_id: prLine.id,
      line_id: uuid(),
      location: data.location,
      location_id: data.location_id,
      location_type: data.location_type,
      status: pendingStatus,
      status_id: pendingStatus.id,
      reference_ids: prLine.reference_ids,
    };

    if (prLine.item_uom) {
      line.item_uom = prLine.item_uom;
      line.item_uom_id = prLine.item_uom_id;
      line.uom_po_quantity = prLine.uom_po_quantity;
      line.uom_quantity = prLine.uom_quantity;
      line.uom_receipt_quantity = prLine.uom_quantity;
      line.uom_unit_cost = prLine.uom_unit_cost
    }
    itemLines.push(line);
  });

  const updatedItemLines = yield call(
    updateAvailableOnHand,
    feathersApp,
    itemLines,
    'available_pre_return',
  );
  const contactId = user.id || user._id;
  const primaryContactNumber = (user.contact_numbers || []).find(
    (number) => number.primary,
  );

  return {
    subsidiary: purchaseReceipt.subsidiary,
    subsidiary_id: purchaseReceipt.subsidiary_id,
    location: purchaseReceipt.location,
    location_id: purchaseReceipt.location_id,
    location_type: purchaseReceipt.location_type,
    supplier: purchaseReceipt.supplier,
    supplier_id: purchaseReceipt.supplier_id,
    currency: purchaseReceipt.currency,
    currency_id: purchaseReceipt.currency_id,
    exchange_rate: purchaseReceipt.exchange_rate,
    status: openStatus,
    status_id: openStatus.id,
    purchase_order: purchaseReceipt.purchase_order,
    purchase_order_id: purchaseReceipt.purchase_order_id,
    purchase_receipt: {
      id: purchaseReceipt.id,
      reference_id: purchaseReceipt.reference_id,
    },
    purchase_receipt_id: purchaseReceipt.id,
    purchase_contact_id: contactId,
    purchase_contact_email: user.email,
    purchase_contact_number: (primaryContactNumber || {}).value,
    lines: updatedItemLines,
    return_type: 'receipt',
    transaction_date: moment().format(),
  };
}

export function* saveInventoryReturnSaga(feathersApp) {
  yield takeEvery(SAVE_INVENTORY_RETURN, callSaveInventoryReturn, feathersApp);
}

export function* callSaveInventoryReturn(feathersApp, action) {
  try {
    const { user } = yield select();
    const { data, redirectToView } = action;

    yield sagasCommon.showProgressSaveRecord();
    let result = yield call(
      apiInventoryReturn.saveInventoryReturn,
      feathersApp,
      { user, data },
    );
    const id = result.id || result[0].id;

    yield put({ type: GET_INVENTORY_RETURN, id });

    if (redirectToView) {
      browserHistory.push(`/admin/purchase/return/view/${id}`);
    } else {
      browserHistory.push(`/admin/purchase/return/edit/${id}`);
    }
    yield sagasCommon.doneSaveRecord(result, 'Purchase return was saved');
  } catch (e) {
    console.error(e.stack);
    yield sagasCommon.showErrorSaveRecord(e);
  }
}

export function* fetchListInventoryReturnSaga(feathersApp) {
  yield takeLatest(
    FETCH_LIST_INVENTORY_RETURN,
    callFetchListInventoryReturn,
    feathersApp,
  );
}

export function* callFetchListInventoryReturn(feathersApp, action) {
  try {
    const { silent, filters } = action;
    yield sagasCommon.showProgressListTable(silent);
    const dataParams = {
      filters,
      list_type: INVENTORY_RETURN,
    };
    const tableData = yield call(apiCommon.getDocs, feathersApp, dataParams);
    yield call(sagasCommon.doneListTable, {
      rows: tableData.data,
      total_rows: tableData.total,
    });
  } catch (e) {
    yield sagasCommon.showErrorListTable(e);
  }
}

export function* getInventoryReturnLineItemLocationSaga(feathersApp) {
  yield takeEvery(
    GET_INVENTORY_RETURN_LINE_ITEM_LOCATION,
    callGetInventoryReturnLineItemLocation,
    feathersApp,
  );
}

export function* callGetInventoryReturnLineItemLocation(feathersApp, action) {
  try {
    const { locationId, itemId, index, itemUom } = action;
    const lineIndex = `lines[${index}]`;
    yield put(change(form, `${lineIndex}.isFetchingItemLocation`, true));
    const itemLocation = yield call(
      apiPurchaseCommon.getItemLocation,
      feathersApp,
      { itemId: itemId.split(':')[0], locationId },
    );

    if (itemLocation) {
      const initialItemLine = yield call(
        initializeItemLine,
        feathersApp,
        itemLocation,
      );
      const { taxGroup, taxRate, averageCost, available } = initialItemLine;
      if (itemUom) {
        const uomAvailableQty = Math.floor(available/itemUom.conversion_rate);
        yield put(
          change(
            form,
            `${lineIndex}.uom_available_pre_return`,
            Number(uomAvailableQty),
          ),
        );
        yield put(change(form, `${lineIndex}.uom_quantity`, 0));
        yield put(change(form, `${lineIndex}.uom_unit_cost`, averageCost*itemUom.conversion_rate));
      }
      yield put(
        change(form, `${lineIndex}.available_pre_return`, available || 0),
      );
      yield put(change(form, `${lineIndex}.tax_group`, taxGroup));
      yield put(change(form, `${lineIndex}.tax_group_id`, taxGroup.id));
      yield put(change(form, `${lineIndex}.tax_rate`, taxRate || 0));
      yield put(change(form, `${lineIndex}.total`, 0));
      yield put(change(form, `${lineIndex}.quantity`, 0));
      yield put(change(form, `${lineIndex}.unit_cost`, averageCost));
      yield put(change(form, `${lineIndex}.isFetchingItemLocation`, false));
    } else {
      yield call(resetLine, index);
      throw new Error('Item is not available in location');
    }
  } catch (e) {
    yield sagasCommon.showErrorSaveRecord(e);
  }
}

function* resetLine(index) {
  yield put(arrayRemove(form, 'lines', index));
}

export function* getAllInventoryReturnLinesItemLocationSaga(feathersApp) {
  yield takeLatest(
    GET_ALL_INVENTORY_RETURN_LINES_ITEM_LOCATION,
    callGetAllInventoryReturnLinesItemLocation,
    feathersApp,
  );
}

export function* callGetAllInventoryReturnLinesItemLocation(
  feathersApp,
  action,
) {
  try {
    const lines = yield select(getFormLines);
    const { locationId } = action;
    const itemLocationsQuery = lines.map((itemLine) => {
      const { item_id } = itemLine;
      return {
        item_id: item_id.split(':')[0],
        location_id: locationId,
      };
    });
    const itemLocations = yield call(
      apiPurchaseCommon.getItemLocations,
      feathersApp,
      itemLocationsQuery,
    );
    const indexedItemLocations = keyBy(
      itemLocations,
      ({ item_id, location_id }) => `${item_id}:${location_id}`,
    );
    let validItemLocations = true;
    const newLines = [];

    for (let line of lines) {
      const newLine = Object.assign({}, line);
      const itemId = line.item_id.split(':')[0];
      const lineItemLocationId = `${itemId}:${locationId}`;
      const itemLocation = indexedItemLocations[lineItemLocationId];

      if (itemLocation) {
        const initialItemLine = yield call(
          initializeItemLine,
          feathersApp,
          itemLocation,
        );
        const { taxGroup, taxRate, averageCost, available } = initialItemLine;
        newLine.available_pre_return = available;
        newLine.tax_group = taxGroup;
        newLine.tax_group_id = taxGroup.id;
        newLine.tax_rate = taxRate;
        newLine.total = 0;
        newLine.quantity = 0;
        newLine.unit_cost = averageCost;
        newLine.unit_price_location = 0;
        if (newLine.item_uom) {
          newLine.uom_unit_cost = averageCost*newLine.item_uom.conversion_rate;
        }
        newLines.push(newLine);
      } else {
        validItemLocations = false;
      }
    }

    yield put(change(form, 'lines', newLines));
    if (!validItemLocations) {
      throw new Error('Item is not available in location');
    }
  } catch (e) {
    yield sagasCommon.showErrorSaveRecord(e);
  }
}

export function* getInventoryReturnLineTaxGroupSaga(feathersApp) {
  yield takeEvery(
    GET_INVENTORY_RETURN_LINE_TAX_GROUP,
    callGetInventoryReturnLineTaxGroup,
    feathersApp,
  );
}

export function* callGetInventoryReturnLineTaxGroup(feathersApp, action) {
  try {
    const { taxGroupId, index } = action;
    const lineIndex = `lines[${index}]`;
    yield put(change(form, `${lineIndex}.isFetchingTaxGroup`, true));
    const taxRate = taxGroupId
      ? yield call(apiTaxCode.getTotalTaxRate, feathersApp, taxGroupId)
      : 0;
    yield put(change(form, `${lineIndex}.tax_rate`, taxRate || 0));
    yield put(change(form, `${lineIndex}.isFetchingTaxGroup`, false));
  } catch (e) {
    yield sagasCommon.showErrorSaveRecord(e);
  }
}

export function* getInventoryReturnExchangeRateSaga(feathersApp) {
  yield takeEvery(
    GET_INVENTORY_RETURN_EXCHANGE_RATE,
    callGetInventoryReturnExchangeRate,
    feathersApp,
  );
}

export function* callGetInventoryReturnExchangeRate(feathersApp, action) {
  try {
    const { currencyFromId, currencyToId, date } = action;
    const exchangeRate = yield call(
      apiExchangeRate.getExchangeRate,
      feathersApp,
      { currencyFromId, currencyToId, date },
    );
    yield put(
      change(form, 'exchange_rate', Number((exchangeRate || {}).rate) || 1),
    );
  } catch (e) {
    yield sagasCommon.showErrorSaveRecord(e);
  }
}
